import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { 
  Bot, 
  Megaphone,
  MessageSquare, 
  Mail, 
  Newspaper,
  Image,
  Globe,
  Sparkles 
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { ComingSoonOverlay } from "@/components/ui/coming-soon-overlay"

interface ToolCard {
  title: string
  description: string
  icon: React.ReactNode
  comingSoon?: boolean
  path?: string
}

const tools: ToolCard[] = [
  {
    title: "Social Media Post Generator",
    description: "AI-powered templates for different campaign types and platforms",
    icon: <MessageSquare className="h-5 w-5 text-pink-500" />,
    path: "/dashboard/ai-engine/marketing-outreach/social-posts"
  },
  {
    title: "Event Promotion Materials",
    description: "Auto-generated banners and visuals for marketing campaigns",
    icon: <Image className="h-5 w-5 text-orange-500" />,
    path: "/dashboard/ai-engine/marketing-outreach/promotion-materials"
  }
]

export default function MarketingOutreachPage({ profile, session }: { profile: any | null, session: any | null }) {
  const navigate = useNavigate()

  return (
    <div className="h-full w-full">
      {/* Content */}
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Megaphone className="h-8 w-8 text-pink-500" />
          <div>
            <h1 className="text-2xl font-bold">AI-Powered Marketing & Outreach</h1>
            <p className="text-muted-foreground">
              Amplify your impact with intelligent marketing tools
            </p>
          </div>
        </div>

        <div className="relative">          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {tools.map((tool) => (
              <Card
                key={tool.title}
                className="group relative overflow-hidden border border-border/50 bg-background/50 backdrop-blur-sm transition-all hover:border-pink-200 hover:shadow-lg"
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="rounded-full bg-pink-50 p-2 transition-colors group-hover:bg-pink-100">
                      {tool.icon}
                    </div>
                    {tool.comingSoon && (
                      <span className="inline-flex items-center rounded-full border border-pink-200 bg-pink-50 px-2.5 py-0.5 text-xs font-medium text-pink-700">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <CardTitle className="text-xl">{tool.title}</CardTitle>
                  <CardDescription>{tool.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    className="w-full bg-gradient-to-r from-pink-500 to-purple-500 text-white transition-all hover:from-pink-600 hover:to-purple-600"
                    disabled={tool.comingSoon}
                    onClick={() => tool.path && navigate(tool.path)}
                  >
                    <Sparkles className="mr-2 h-4 w-4" />
                    {tool.comingSoon ? "Coming Soon" : "Get Started"}
                  </Button>
                </CardContent>

                {/* Decorative corner gradients */}
                <div className="absolute -right-6 -top-6 h-12 w-12 rounded-full bg-gradient-to-br from-pink-200 to-transparent opacity-50 blur-lg transition-all group-hover:opacity-100" />
                <div className="absolute -bottom-6 -left-6 h-12 w-12 rounded-full bg-gradient-to-tr from-purple-200 to-transparent opacity-50 blur-lg transition-all group-hover:opacity-100" />
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
